import { useEffect, useMemo, useState } from 'react';
import {
  Box, Button, Checkbox, Drawer, FormControlLabel, IconButton, MenuItem, Stack, TextField, Typography,
} from '@mui/material';
import { Close } from '@mui/icons-material';
import publicListingsService from '../../services/publicListingsService';
import { brand, border, text } from '../../theme/colors';
import { BATH_CHOICES, BED_CHOICES, CountChoice, TYPE_OPTIONS, featureList } from './FilterRow';
import { INK } from './SearchHeader';

// "Must have" choices: the features buyers ask about most, across houses and
// stands. The keys are what the browse API matches (walled covers Durawall,
// solar covers Solar backup).
export const MUST_HAVE = [
  ['borehole', 'Borehole'], ['solar', 'Solar backup'], ['generator', 'Generator'], ['staff quarters', 'Staff quarters'],
  ['swimming pool', 'Swimming pool'], ['walled', 'Walled / durawall'], ['electric fence', 'Electric fence'],
  ['council water', 'Council water'], ['sewer connected', 'Sewer connected'], ['fibre internet', 'Fibre internet'],
  ['serviced', 'Serviced stand'],
];

export const PANEL_KEYS = ['min_price', 'max_price', 'include_por', 'type', 'beds_min', 'baths_min', 'min_size', 'max_size',
  'features', 'listed_within', 'hide_sold', 'tenure'];

const pick = (values) => Object.fromEntries(PANEL_KEYS.map((k) => [k, values[k] || '']));

/** How many panel filters are set — the badge on the Filters button. */
export const countActive = (values) => PANEL_KEYS
  .filter((k) => k !== 'include_por' && k !== 'max_price' && k !== 'max_size')
  .reduce((n, k) => {
    if (k === 'features') return n + featureList(values.features).length;
    if (k === 'min_price') return n + (values.min_price || values.max_price ? 1 : 0);
    if (k === 'min_size') return n + (values.min_size || values.max_size ? 1 : 0);
    return n + (values[k] ? 1 : 0);
  }, 0);

const Section = ({ title, children }) => (
  <Box component="fieldset" sx={{ m: 0, p: 0, border: 0 }}>
    <Typography component="legend" sx={{ fontWeight: 700, fontSize: 16, mb: 1.25, p: 0 }}>{title}</Typography>
    {children}
  </Box>
);

/**
 * The full Filters panel. It edits a draft and shows, live, how many homes the
 * draft would find — the footer button says "Show 4 homes", not "Apply".
 * Nothing touches the URL until the buyer presses it.
 */
export default function FiltersDrawer({ open, values = {}, baseQuery = {}, onClose, onApply }) {
  const [draft, setDraft] = useState(() => pick(values));
  const [count, setCount] = useState(null);

  useEffect(() => { if (open) setDraft(pick(values)); }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  const set = (k, v) => setDraft((d) => ({ ...d, [k]: v }));
  const features = featureList(draft.features);
  const toggleFeature = (f) => set('features', (features.includes(f) ? features.filter((x) => x !== f) : [...features, f]).join(','));

  // Live count for the footer, a moment after the buyer stops changing things.
  const query = useMemo(() => {
    const q = { ...baseQuery, limit: 1 };
    Object.entries(draft).forEach(([k, v]) => { if (v) q[k] = v; else delete q[k]; });
    return q;
  }, [draft, baseQuery]);
  useEffect(() => {
    if (!open) return undefined;
    let live = true;
    setCount(null);
    const t = setTimeout(() => {
      publicListingsService.list(query)
        .then((res) => { if (live) setCount(res.data?.total ?? null); })
        .catch(() => { if (live) setCount(null); });
    }, 350);
    return () => { live = false; clearTimeout(t); };
  }, [open, query]);

  const showLabel = count == null ? 'Show homes' : `Show ${count} ${count === 1 ? 'home' : 'homes'}`;

  return (
    <Drawer anchor="right" open={open} onClose={onClose} PaperProps={{ sx: { width: { xs: '100%', sm: 480 } } }}>
      <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }} role="dialog" aria-label="All filters">
        <Box sx={{ px: 3, height: 68, display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderBottom: `1px solid ${border.default}` }}>
          <Typography component="h2" sx={{ fontSize: 20, fontWeight: 700 }}>Filters</Typography>
          <IconButton onClick={onClose} aria-label="Close filters"><Close /></IconButton>
        </Box>

        <Stack spacing={3.25} sx={{ flex: 1, overflowY: 'auto', px: 3, py: 3 }}>
          <Section title="Price (US$)">
            <Stack direction="row" spacing={1.5} alignItems="center">
              <TextField fullWidth size="small" type="number" label="Minimum" value={draft.min_price}
                inputProps={{ min: 0 }} onChange={(e) => set('min_price', e.target.value)} />
              <Typography sx={{ color: text.secondary }}>to</Typography>
              <TextField fullWidth size="small" type="number" label="Maximum" value={draft.max_price}
                inputProps={{ min: 0 }} onChange={(e) => set('max_price', e.target.value)} />
            </Stack>
            <FormControlLabel sx={{ mt: 0.5 }}
              control={<Checkbox checked={draft.include_por === '1'} onChange={(e) => set('include_por', e.target.checked ? '1' : '')} />}
              label="Include homes with price on request" />
          </Section>

          <Section title="Home type">
            <Stack direction="row" spacing={1}>
              {TYPE_OPTIONS.map((o) => {
                const on = (draft.type || '') === o.value;
                return (
                  <Button key={o.value || 'all'} fullWidth onClick={() => set('type', o.value)} aria-pressed={on}
                    sx={{ height: 46, textTransform: 'none', fontSize: 15, borderRadius: '10px',
                      fontWeight: on ? 700 : 500, color: on ? '#134e4a' : INK,
                      border: on ? `1.5px solid ${brand.primaryDark}` : '1px solid #b8c4c2', bgcolor: on ? '#e6f4f2' : '#fff' }}>
                    {o.label}
                  </Button>
                );
              })}
            </Stack>
          </Section>

          <CountChoice label="Bedrooms" choices={BED_CHOICES} value={draft.beds_min} onChange={(v) => set('beds_min', v)} />
          <CountChoice label="Bathrooms" choices={BATH_CHOICES} value={draft.baths_min} onChange={(v) => set('baths_min', v)} />

          <Section title="Stand size (m²)">
            <Stack direction="row" spacing={1.5} alignItems="center">
              <TextField fullWidth size="small" type="number" label="Minimum" value={draft.min_size}
                inputProps={{ min: 0 }} onChange={(e) => set('min_size', e.target.value)} />
              <Typography sx={{ color: text.secondary }}>to</Typography>
              <TextField fullWidth size="small" type="number" label="Maximum" value={draft.max_size}
                inputProps={{ min: 0 }} onChange={(e) => set('max_size', e.target.value)} />
            </Stack>
          </Section>

          <Section title="Must have">
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              {MUST_HAVE.map(([key, label]) => {
                const on = features.includes(key);
                return (
                  <Button key={key} onClick={() => toggleFeature(key)} aria-pressed={on}
                    sx={{ height: 40, px: 1.75, borderRadius: '10px', textTransform: 'none', fontSize: 14,
                      fontWeight: on ? 700 : 500, color: on ? '#134e4a' : INK,
                      border: on ? `1.5px solid ${brand.primaryDark}` : '1px solid #b8c4c2', bgcolor: on ? '#e6f4f2' : '#fff' }}>
                    {label}
                  </Button>
                );
              })}
            </Box>
          </Section>

          <Section title="Listing">
            <TextField select fullWidth size="small" label="Listed within" value={draft.listed_within}
              onChange={(e) => set('listed_within', e.target.value)}>
              <MenuItem value="">Any time</MenuItem>
              <MenuItem value="14">Last 14 days</MenuItem>
              <MenuItem value="30">Last 30 days</MenuItem>
            </TextField>
            <FormControlLabel sx={{ mt: 1 }}
              control={<Checkbox checked={draft.hide_sold === '1'} onChange={(e) => set('hide_sold', e.target.checked ? '1' : '')} />}
              label="Hide homes sold in the last 30 days" />
            <FormControlLabel
              control={<Checkbox checked={draft.tenure === 'title_deed'} onChange={(e) => set('tenure', e.target.checked ? 'title_deed' : '')} />}
              label="Title deeds only (hide cessions)" />
          </Section>
        </Stack>

        <Box sx={{ px: 3, py: 2, borderTop: `1px solid ${border.default}`, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Button onClick={() => setDraft(pick({}))} sx={{ textTransform: 'none', color: INK, fontWeight: 600, textDecoration: 'underline' }}>
            Clear all
          </Button>
          <Button onClick={() => onApply(draft)} variant="contained"
            sx={{ height: 52, px: 3.5, borderRadius: '10px', textTransform: 'none', fontWeight: 700, fontSize: 16, bgcolor: brand.primaryDark, '&:hover': { bgcolor: '#134e4a' } }}>
            {showLabel}
          </Button>
        </Box>
      </Box>
    </Drawer>
  );
}
